import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "./cn";

const cardVariants = cva("relative overflow-hidden", {
  variants: {
    tone: {
      paper: "bg-paper text-ink",
      mist: "bg-mist text-ink",
      ink: "bg-ink text-paper",
      brand: "bg-lime text-ink",
      urgent: "bg-ember text-paper",
      sun: "bg-sun text-ink",
    },
    padding: {
      none: "",
      sm: "p-4",
      md: "p-5",
      lg: "p-6",
      xl: "p-8",
    },
    radius: {
      md: "rounded-[12px]",
      lg: "rounded-[20px]",
      xl: "rounded-[24px]",
    },
    bordered: {
      true: "border-[1.5px] border-smoke",
    },
  },
  defaultVariants: {
    tone: "paper",
    padding: "lg",
    radius: "xl",
  },
});

export type CardProps = React.HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof cardVariants>;

export const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, tone, padding, radius, bordered, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        cardVariants({ tone, padding, radius, bordered }),
        className,
      )}
      {...props}
    />
  ),
);
Card.displayName = "Card";

export const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("mb-4 flex items-start justify-between gap-3", className)}
    {...props}
  />
));
CardHeader.displayName = "CardHeader";

export const CardTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn(
      "text-[20px] font-extrabold tracking-tight leading-tight",
      className,
    )}
    {...props}
  />
));
CardTitle.displayName = "CardTitle";

export const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p ref={ref} className={cn("text-sm text-ash", className)} {...props} />
));
CardDescription.displayName = "CardDescription";

export const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("mt-5 flex flex-wrap items-center gap-2", className)}
    {...props}
  />
));
CardFooter.displayName = "CardFooter";

export { cardVariants };
